import * as THREE from "three";

export interface Eigen3 {
  /** Eigenvalues sorted descending. */
  values: [number, number, number];
  /** Unit eigenvectors matching `values`. */
  vectors: [THREE.Vector3, THREE.Vector3, THREE.Vector3];
}

/** Cyclic Jacobi eigen-decomposition of a symmetric 3×3 matrix given row-major. */
export function symmetricEigen3(m: ArrayLike<number>): Eigen3 {
  const a = [
    [m[0], m[1], m[2]],
    [m[3], m[4], m[5]],
    [m[6], m[7], m[8]],
  ];
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];

  for (let sweep = 0; sweep < 60; sweep++) {
    const off = a[0][1] * a[0][1] + a[0][2] * a[0][2] + a[1][2] * a[1][2];
    const scale = a[0][0] * a[0][0] + a[1][1] * a[1][1] + a[2][2] * a[2][2];
    if (off <= 1e-24 * Math.max(1e-30, scale)) break;

    for (let p = 0; p < 2; p++) {
      for (let q = p + 1; q < 3; q++) {
        const apq = a[p][q];
        if (Math.abs(apq) < 1e-300) continue;
        const theta = (a[q][q] - a[p][p]) / (2 * apq);
        const t =
          (theta >= 0 ? 1 : -1) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;
        for (let k = 0; k < 3; k++) {
          const akp = a[k][p];
          const akq = a[k][q];
          a[k][p] = c * akp - s * akq;
          a[k][q] = s * akp + c * akq;
        }
        for (let k = 0; k < 3; k++) {
          const apk = a[p][k];
          const aqk = a[q][k];
          a[p][k] = c * apk - s * aqk;
          a[q][k] = s * apk + c * aqk;
        }
        for (let k = 0; k < 3; k++) {
          const vkp = v[k][p];
          const vkq = v[k][q];
          v[k][p] = c * vkp - s * vkq;
          v[k][q] = s * vkp + c * vkq;
        }
      }
    }
  }

  const pairs = [0, 1, 2]
    .map((i) => ({
      value: a[i][i],
      vector: new THREE.Vector3(v[0][i], v[1][i], v[2][i]).normalize(),
    }))
    .sort((x, y) => y.value - x.value);

  return {
    values: [pairs[0].value, pairs[1].value, pairs[2].value],
    vectors: [pairs[0].vector, pairs[1].vector, pairs[2].vector],
  };
}

export interface PrincipalAxes extends Eigen3 {
  mean: THREE.Vector3;
  count: number;
}

/** PCA of a flat xyz array, optionally weighted. */
export function principalAxes(
  xyz: ArrayLike<number>,
  count: number,
  weights?: ArrayLike<number>
): PrincipalAxes {
  let wSum = 0;
  let mx = 0;
  let my = 0;
  let mz = 0;
  for (let i = 0; i < count; i++) {
    const w = weights ? weights[i] : 1;
    wSum += w;
    mx += w * xyz[i * 3];
    my += w * xyz[i * 3 + 1];
    mz += w * xyz[i * 3 + 2];
  }
  const inv = wSum > 0 ? 1 / wSum : 0;
  mx *= inv;
  my *= inv;
  mz *= inv;

  let cxx = 0;
  let cxy = 0;
  let cxz = 0;
  let cyy = 0;
  let cyz = 0;
  let czz = 0;
  for (let i = 0; i < count; i++) {
    const w = weights ? weights[i] : 1;
    const dx = xyz[i * 3] - mx;
    const dy = xyz[i * 3 + 1] - my;
    const dz = xyz[i * 3 + 2] - mz;
    cxx += w * dx * dx;
    cxy += w * dx * dy;
    cxz += w * dx * dz;
    cyy += w * dy * dy;
    cyz += w * dy * dz;
    czz += w * dz * dz;
  }
  const eig = symmetricEigen3([
    cxx * inv, cxy * inv, cxz * inv,
    cxy * inv, cyy * inv, cyz * inv,
    cxz * inv, cyz * inv, czz * inv,
  ]);
  return { ...eig, mean: new THREE.Vector3(mx, my, mz), count };
}

/**
 * Solve A x = b in place for a small dense system (row-major `a` of size n×n).
 * Returns null when the matrix is singular.
 */
export function solveLinearSystem(
  a: Float64Array,
  b: Float64Array,
  n: number
): Float64Array | null {
  const m = new Float64Array(a);
  const x = new Float64Array(b);
  for (let col = 0; col < n; col++) {
    let pivot = col;
    let best = Math.abs(m[col * n + col]);
    for (let r = col + 1; r < n; r++) {
      const v = Math.abs(m[r * n + col]);
      if (v > best) {
        best = v;
        pivot = r;
      }
    }
    if (best < 1e-18) return null;
    if (pivot !== col) {
      for (let k = 0; k < n; k++) {
        const t = m[col * n + k];
        m[col * n + k] = m[pivot * n + k];
        m[pivot * n + k] = t;
      }
      const t = x[col];
      x[col] = x[pivot];
      x[pivot] = t;
    }
    const diag = m[col * n + col];
    for (let r = col + 1; r < n; r++) {
      const f = m[r * n + col] / diag;
      if (f === 0) continue;
      for (let k = col; k < n; k++) m[r * n + k] -= f * m[col * n + k];
      x[r] -= f * x[col];
    }
  }
  for (let r = n - 1; r >= 0; r--) {
    let s = x[r];
    for (let k = r + 1; k < n; k++) s -= m[r * n + k] * x[k];
    x[r] = s / m[r * n + r];
  }
  return x;
}

/** Rigid increment: rotate by axis-angle vector `w` (radians) about `pivot`, then translate by `t`. */
export function rigidIncrement(
  w: THREE.Vector3,
  t: THREE.Vector3,
  pivot: THREE.Vector3,
  out = new THREE.Matrix4()
): THREE.Matrix4 {
  const angle = w.length();
  const rot = new THREE.Matrix4();
  if (angle > 1e-15) {
    rot.makeRotationAxis(w.clone().divideScalar(angle), angle);
  }
  const toOrigin = new THREE.Matrix4().makeTranslation(-pivot.x, -pivot.y, -pivot.z);
  const back = new THREE.Matrix4().makeTranslation(
    pivot.x + t.x,
    pivot.y + t.y,
    pivot.z + t.z
  );
  return out.multiplyMatrices(back, rot).multiply(toOrigin);
}

/** Deterministic PRNG so registrations and tests are reproducible. */
export function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function matrixToRowMajor(m: THREE.Matrix4): number[][] {
  const e = m.elements;
  return [
    [e[0], e[4], e[8], e[12]],
    [e[1], e[5], e[9], e[13]],
    [e[2], e[6], e[10], e[14]],
    [e[3], e[7], e[11], e[15]],
  ];
}
